import { Sparkles, RotateCcw } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { Problem } from '@/lib/api/problems';

// PromptEditorPanel is the Prompt Arena counterpart to the Monaco code editor:
// a plain-language prompt composer (not a code widget — no line numbers, no
// syntax highlighting) framed around "this is what you say to the model",
// since a prompt-mode problem grades the instruction itself, not code.
export function PromptEditorPanel({
  problem,
  value,
  onChange,
  onReset,
}: {
  problem: Problem;
  value: string;
  onChange: (value: string) => void;
  onReset: () => void;
}) {
  const sampleInputs = problem.prompt_rubric?.test_inputs ?? [];

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-none">Your prompt</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              This is what gets sent to the model — write it like an instruction, not code.
            </p>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Reset to starter prompt">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset prompt to the starter version?</AlertDialogTitle>
              <AlertDialogDescription>Your changes will be lost. This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onReset}>Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Write the prompt you want to send to the model..."
        className="min-h-[220px] flex-1 resize-none rounded-lg border-border bg-muted/20 p-4 font-sans text-[15px] leading-relaxed shadow-none focus-visible:ring-1"
        spellCheck={false}
      />

      <p className="mt-2 text-xs text-muted-foreground">
        Use <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{'{{input}}'}</code> anywhere in
        your prompt — it gets replaced with each message below when you run it.
      </p>

      {sampleInputs.length > 0 && (
        <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            Messages this prompt will be tested against
          </p>
          <div className="space-y-2">
            {sampleInputs.map((input, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md bg-background px-3 py-2 text-sm shadow-sm">
                <span className="mt-0.5 text-xs font-medium text-muted-foreground">{i + 1}.</span>
                <span>{input}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
